<script setup lang="ts">
import { useProductCounter } from '@/composables/useProductCounter'

import { useCityStore } from '@/store/city/city'

import { AdditiveItem } from '@/types/api/api-v3/basket'

type Props = {
  additives: AdditiveItem[]
  additive: AdditiveItem
}

const props = defineProps<Props>()

const { isMobile } = useResponsive()

const cityStore = useCityStore()

const productId = computed(() => props.additive.product_id)

const {
  quantitySelectedProducts,
  setProductQuantity,
  decreaseQuantity,
  increaseQuantity,
} = useProductCounter(productId)

const currencySymbol = computed(() => {
  return cityStore.currencySymbol
})

const additivePrice = computed(() => {
  return props.additive.total_price === 0
    ? `0 ${currencySymbol.value}`
    : `+${props.additive.total_price} ${currencySymbol.value}`
})

const additives = computed(() => props.additives)

const isErrorImage = ref(false)

const productImage = computed(() => {
  if (isErrorImage.value || !props.additive.image_url) {
    return '/default-image-desktop.png'
  }

  return props.additive.image_url
})

watch(additives, () => {
  for (const additive of additives.value) {
    /* Изменяем значение только для текущей добавки */
    if (additive.product_id === productId.value) {
      setProductQuantity(additive.total_quantity)
      return
    }
  }
})

onMounted(() => {
  setProductQuantity(props.additive.total_quantity)
})
</script>

<template>
  <div class="additive">
    <nuxt-img
      :src="productImage"
      :alt="additive.title"
      class="additive__image"
      @error="isErrorImage = true"
    />

    <p class="additive__price">
      {{ additivePrice }}
    </p>

    <p class="additive__title" :title="additive.title">{{ additive.title }}</p>

    <div class="additive__button">
      <the-counter
        :min="0"
        :value="quantitySelectedProducts"
        :size="isMobile ? 'xs' : 'sm'"
        variant="secondary"
        @on-decrease="decreaseQuantity"
        @on-increase="increaseQuantity"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.additive {
  width: 100%;
  max-width: 128px;
  max-height: 206px;
  padding: 8px;
  font-size: 14px;
  font-weight: 600;
  background-color: var(--ui-grey);
  border-radius: 16px;

  &__image {
    width: 100%;
    height: 76px;
    object-fit: contain;
  }

  &__price {
    margin-bottom: 4px;
    font-weight: 800;
    color: var(--ui-text-primary);
  }

  &__title {
    margin-bottom: 16px;
    overflow: hidden;
    color: var(--ui-text-primary);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__button {
    display: flex;
    justify-content: center;

    :deep(.app-counter) {
      min-width: 100%;
      height: 40px;
    }
  }

  @include media('<tablet') {
    width: 100%;
    min-width: 100px;
  }
}
</style>
